import MuiAutocomplete from '@mui/material/Autocomplete';
import { type SxProps, type Theme } from '@mui/material';
import TextField from '../TextField';
import { Box } from '../../layout/Box';
import { CaretDownIcon, CheckIcon } from '../../icons/lingodaIcons';
import { useTheme } from '../../hooks';
import type { TextFieldProps } from '../TextField';
import type { AutocompleteProps as MuiAutocompleteProps } from '@mui/material/Autocomplete';
import type { ReactNode } from 'react';

export { autocompleteClasses } from '@mui/material/Autocomplete';
export type { AutocompleteProps as MuiAutocompleteProps } from '@mui/material/Autocomplete';

export interface AutocompleteProps<T>
    extends MuiAutocompleteProps<T, boolean, boolean, boolean>,
        Pick<TextFieldProps, 'label' | 'placeholder'> {
    key?: string;
    getOptionIcon?: (value: T) => ReactNode;
    hideCheckMark?: boolean;
    textFieldSx?: SxProps<Theme>;
}

export const Autocomplete = <T,>({
    label,
    placeholder,
    getOptionIcon,
    getOptionLabel,
    hideCheckMark,
    textFieldSx,
    ...rest
}: Omit<AutocompleteProps<T>, 'renderInput' | 'renderOption'>) => {
    const theme = useTheme();
    const textSx: SxProps<Theme> = [
        {
            '& .MuiAutocomplete-endAdornment': {
                top: '50%',
                '& .MuiAutocomplete-popupIndicatorOpen': {
                    transform: 'rotate(180deg)',
                },
            },
        },
        ...(Array.isArray(textFieldSx) ? textFieldSx : [textFieldSx]),
    ].filter(Boolean);

    const renderInput: AutocompleteProps<T>['renderInput'] = (params) => (
        <TextField label={label} placeholder={placeholder} sx={textSx} {...params} />
    );

    const renderOption: AutocompleteProps<T>['renderOption'] = (props, value, { selected }) => (
        <li {...props} key={(props as { key?: string }).key}>
            <Box flex={1}>
                {getOptionIcon?.(value)} {getOptionLabel?.(value) || getFallbackLabel(value)}
            </Box>
            {selected && !hideCheckMark && (
                <CheckIcon size={24} color={theme.palette.primary.main} />
            )}
        </li>
    );

    return (
        <MuiAutocomplete
            popupIcon={<CaretDownIcon size={16} />}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            disableClearable
            {...rest}
        />
    );
};

const getFallbackLabel = (value: unknown) =>
    typeof value === 'string' || typeof value === 'number' ? value : '';
